import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useIsFetching, useMutation, useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { NotificationUtils, Skeleton } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { Formik } from "formik"

import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { DataFileUpload } from "../features/DataFiles/DataFileUpload"
import { useFileContent } from "../features/DataFiles/hooks/Queries"
import { FILE_SIZE_LIMIT_IN_BYTES } from "../features/FileDetailsView/Constants"
import { DetailView } from "../features/FileDetailsView/DetailView"
import { formateJsonIntoTableData } from "../features/FileDetailsView/Helpers"
import { KonanAPI } from "../services/KonanAPI"
import { queryClient } from "../store/QueryClientStore"
import type { VersionChangeRequest } from "../types/custom/rules"
import type { DataFileGroup } from "../types/generated/api/DataFileGroup"
import type { ExportJobRetrieve } from "../types/generated/api/ExportJobRetrieve"
import type { TrainingDataUpload } from "../types/generated/api/TrainingDataUpload"

export type Tab = "json" | "table"

export type FilesTab = "Export-data-files" | "Training-data" | "Data-files"

const FILES_RETRIEVE_MAPPER = {
  "Export-data-files": ({ projectId, fileId }: { projectId: string; fileId: string }) =>
    KonanAPI.retrieveExportJob({ project_uuid: projectId, export_job_uuid: fileId }),
  "Training-data": ({ projectId, fileId }: { projectId: string; fileId: string }) =>
    KonanAPI.fetchSingleTrainingData(projectId, fileId),
  "Data-files": ({ projectId, fileId }: { projectId: string; fileId: string }) =>
    KonanAPI.fetchDataFile(projectId, fileId),
} as const

export const DataFileLoader = (): ReactElement => {
  return (
    <Grid container gap={2} p={2}>
      <Grid item width={"100%"}>
        <Skeleton marginBottom={16} height={64} />
      </Grid>
      <Grid item width={"100%"}>
        <Skeleton marginBottom={16} />
      </Grid>
      <Grid item width={"100%"}>
        <Skeleton marginBottom={16} />
        <Skeleton marginBottom={16} />
        <Skeleton marginBottom={16} />
        <Skeleton marginBottom={16} />
        <Skeleton marginBottom={16} />
      </Grid>
    </Grid>
  )
}

/**
 * A page to view the details of a file.
 *
 * This page is responsible for retrieving the file details from the server and
 * displaying the details in a table. It also allows the user to view the file
 * content in a text area.
 *
 * @example
 * <FilesView />
 *
 * @returns {ReactElement} a React Element
 */
export function FilesView(): ReactElement {
  const { id: projectId } = useParams()

  const [searchParams] = useSearchParams()

  const [tab, setTab] = useState<Tab>("json")
  const [errorMsg, setErrorMsg] = useState("")
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState<boolean>(false)

  const fileId = searchParams.get("file_id")

  const currentFilesTab = searchParams.get("page") as FilesTab | undefined

  const isFetchingVersion = useIsFetching({ queryKey: ["user"] })

  // retrieve file details job
  const { data: fileData, isFetching: isFileDataLoading } = useQuery<
    AxiosResponse<ExportJobRetrieve | TrainingDataUpload | DataFileGroup>,
    AxiosError,
    {
      createdBy: string
      createdAt: string
      fileName: string
      fileUrl: string
      fileExtension?: string
      type?: DataFileGroup.type | ExportJobRetrieve.type
      version?: string
    }
  >({
    queryKey: ["file-retrieve", projectId, fileId],
    queryFn: () =>
      FILES_RETRIEVE_MAPPER[currentFilesTab ?? "Data-files"]({
        projectId,
        fileId,
      } as {
        projectId: string
        fileId: string
      }),
    select({ data }) {
      let fileUrl: string | null | undefined = ""

      if ("predictions_file" in data) {
        fileUrl = data.predictions_file
      }

      if ("training_data_file" in data) {
        fileUrl = data.training_data_file
      }

      if ("active_version" in data) {
        fileUrl = data.active_version.data_file
      }

      return {
        createdBy: data.created_by,
        createdAt: data.created_at,
        fileName: data.name,
        fileUrl: fileUrl ?? "",
        version: "active_version" in data ? data.active_version.version : undefined,
        fileExtension:
          "predictions_file" in data
            ? data.data_type?.toLowerCase()
            : "active_version" in data
              ? data.active_version.name.split(".").at(-1)?.toLowerCase()
              : data.name.split(".").at(-1)?.toLowerCase(),
        ...data,
      }
    },
    enabled:
      (currentFilesTab === "Export-data-files" ||
        currentFilesTab === "Training-data" ||
        currentFilesTab === "Data-files") &&
      !!fileId,
  })

  // change ruleset version
  const dataFileVersionChangeMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["file-retrieve", projectId, fileId])
        await queryClient.invalidateQueries(["data-file-versions", fileId])

        NotificationUtils.toast(`Version restored successfully.`, {
          severity: "success",
        })
      },
    },
  )

  const { data: fileContent, isFetching: isFileContentLoading } = useFileContent({
    fileData,
    tab,
    setErrorMsg,
    formateJsonIntoTableData,
  })

  const fileSizeLimitInMB = +(FILE_SIZE_LIMIT_IN_BYTES / 1000000).toFixed(2)

  if (isFileDataLoading || isFileContentLoading || isFetchingVersion) return <DataFileLoader />

  return (
    <Fragment>
      <DataFileUpload
        open={isUpdateDialogOpen}
        onClose={() => setIsUpdateDialogOpen(false)}
        type={fileData?.type as DataFileGroup.type}
      />

      <section>
        {fileData?.fileExtension !== "csv" && fileData?.fileExtension !== "json" ? (
          <KonanEmptyState title={"File type not supported"} />
        ) : !fileData.fileUrl || !!errorMsg ? (
          <KonanEmptyState title={errorMsg ? errorMsg : "No file found"} />
        ) : (
          // TODO:: make this a context instead of Formik
          <Formik
            initialValues={{
              id: fileId,
              fileExtension: fileData?.fileExtension,
              fileContent: fileContent,
              fileUrl: fileData?.fileUrl ?? "",
              fileName: fileData.fileName,
              createdBy: fileData?.createdBy ?? "",
              createdAt: fileData?.createdAt ?? "",
              version: fileData.version ?? undefined,
              activeVersion: fileData.version,
              type: fileData.type,
              isDataFileVersionFetching: false,
            }}
            onSubmit={() => console.warn("Submission isn't supported")}
            key={tab}
          >
            <DetailView
              fileSizeLimit={fileSizeLimitInMB}
              tab={tab}
              setTab={setTab}
              handleEditFile={() => setIsUpdateDialogOpen(true)}
              handleVersionRestore={(version: string) => {
                dataFileVersionChangeMutation.mutate({
                  projectId: projectId as string,
                  resource: "data-files",
                  resourceId: fileId as string,
                  version: version,
                })
              }}
              isVersionRestoreLoading={dataFileVersionChangeMutation.isLoading}
            />
          </Formik>
        )}
      </section>
    </Fragment>
  )
}
